import { useMemo } from "react"

import { Trans } from "react-i18next"
import { useLocation } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { DEFAULT_SIGN_ICON } from "@l2r-front/l2r-map"
import { useNetworksStateContext, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { Campaign } from "@l2r-front/l2r-proptypes"
import { CatalogList, CatalogListError, CatalogListSkeleton } from "@l2r-front/l2r-ui"
import { getAscendantCodes } from "@l2r-front/l2r-utils"

import { APP_CITY } from "../../../../common/constants/appCity"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { API_MODULE_HORIZONTAL_SIGNING, MODULE_HORIZONTAL_SIGNING } from "../../../../common/constants/modules"
import { HorizontalSignsCount } from "../../containers/HorizontalSignsCount"
import { useHorizontalSigningCategoriesStats } from "../../hooks/queries/horizontalSigning/useHorizontalSigningCategoriesStats"
import { useHorizontalSignTypes } from "../../hooks/queries/horizontalSigning/useHorizontalSignTypes"

import * as Styled from "./HorizontalSignCampaignCatalog.styled"

interface HorizontalSignCampaignCatalogProps {
    campaign: Campaign
    className?: string
    campaignFilters?: any
    setCampaignFilters?: (filters: any) => void
}

export function HorizontalSignCampaignCatalog(props: HorizontalSignCampaignCatalogProps) {

    const {
        className,
        campaign,
        campaignFilters,
        setCampaignFilters,
    } = props

    const { data: horizontalSignsStats, isLoading: isLoadingHorizontalSignsStats, isError: isErrorHorizontalSignsStats } = useHorizontalSigningCategoriesStats({ campaign: campaign.uuid }, { enabled: !!campaign.uuid, filterBySubnetworks: false })
    const { data: horizontalSignTypes, isLoading: isLoadingHorizontalSignTypes, isError: isErrorHorizontalSignTypes } = useHorizontalSignTypes()
    const typeFilter = campaignFilters?.type
    const ascendantCodes = getAscendantCodes(typeFilter, horizontalSignTypes)
    const { t } = useTranslation(I18N_NAMESPACE)
    const { selectedNetwork, selectedSubNetworkSlugs } = useNetworksStateContext()
    const readOnly = useRoleIsReadOnly(selectedSubNetworkSlugs, API_MODULE_HORIZONTAL_SIGNING)
    const { search } = useLocation()

    const filteredHorizontalSignTypes = useMemo(() => {
        if (!horizontalSignsStats || !horizontalSignTypes) {
            return []
        } else {
            return horizontalSignTypes
                .map(parent => ({
                    ...parent,
                    children: parent.children.filter(
                        child => (horizontalSignsStats.count[child.code] || 0) > 0
                    )
                }))
                .filter(
                    parent =>
                        (horizontalSignsStats.count[parent.code] || 0) > 0 ||
                        parent.children.length > 0
                );
        }
    }, [horizontalSignsStats, horizontalSignTypes])

    const handleFilterChange = (code: string | null) => {
        if (typeFilter === code || ascendantCodes.indexOf(code) >= 0) {
            const filterIndex = ascendantCodes.indexOf(code)
            if (filterIndex !== ascendantCodes.length - 1) {
                setCampaignFilters({ ...campaignFilters, type: ascendantCodes[filterIndex + 1] })
            } else {
                setCampaignFilters({ ...campaignFilters, type: null })
            }
        } else {
            setCampaignFilters({ ...campaignFilters, type: code })
        }
    }

    const EmptyListComponent = useMemo(() => {
        return <Styled.Info>
            {readOnly ?
                t(I18N_NAMESPACE, "containers.horizontalSignTypeList.emptyReadOnly")
                :
                <Trans
                    ns={I18N_NAMESPACE}
                    i18nKey="containers.horizontalSignTypeList.empty"
                    components={[<Styled.ErrorLink to={`/${selectedNetwork.slug}/${APP_CITY}/${MODULE_HORIZONTAL_SIGNING}/`} />]}
                />
            }
        </Styled.Info>
    }, [selectedNetwork.slug, readOnly, t])

    if (isErrorHorizontalSignTypes && !isErrorHorizontalSignsStats) {
        return <CatalogListError className={className} errorText={t(I18N_NAMESPACE, "containers.horizontalSignTypeList.error")} />
    }

    if (isLoadingHorizontalSignTypes || isLoadingHorizontalSignsStats
    ) {
        return <CatalogListSkeleton className={className} />
    }

    return <CatalogList
        ascendantCodes={ascendantCodes}
        onClick={handleFilterChange}
        className={className}
        data={filteredHorizontalSignTypes}
        defaultIcon={DEFAULT_SIGN_ICON}
        emptyListComponent={EmptyListComponent}
        filter={typeFilter}
        title={t(I18N_NAMESPACE, "containers.horizontalSignTypeList.title")}
        titleNavigationUrl={`/${selectedNetwork.slug}/${APP_CITY}/${MODULE_HORIZONTAL_SIGNING}/${search}`}
        tooltipText={(filteredHorizontalSignTypes?.length && !readOnly) ? t(I18N_NAMESPACE, "containers.horizontalSignTypeList.tooltip") : undefined}
        showFilteredCount={false}
        ItemsCountComponent={HorizontalSignsCount}
        ItemsCountComponentProps={
            {
                displayFilteredCount: false,
                displayTotalCount: true,
                filterBySubnetworks: false,
            }}
    />
}
